/** Canonical project stages used in Stages tab and progress calculations. */
export const CANONICAL_STAGE_NAMES = new Set([
  'Planning',
  'Design',
  'Consent',
  'Construction',
  'Handover',
  'Completed',
]);

/** Schedule items with no explicit or inferable stage — must not default to Planning. */
export const UNASSIGNED_STAGE = 'Unassigned';

export interface ScheduleItemStageInput {
  title: string;
  main_stage?: string | null;
  phase?: string | null;
  source_template_item_id?: string | null;
}

/**
 * Infer the main stage from a schedule item title.
 * Returns null when the title does not match any known pattern.
 */
export function inferStageFromTitle(title: string): string | null {
  const lower = title.toLowerCase();

  if (/(\bprecon\b|\bcontract\b|\bowner\b|\bsite prep\b|\bservice disconnect\b|\bh&s\b|\bhealth and safety\b|\bsetup\b|\bkickoff\b|\bapplication\b|\bdeposit\b)/.test(lower)) {
    return 'Planning';
  }

  if (/(\bdesign\b|\bengineering\b|\bgeotech\b|\bdrawings?\b|\bspecification\b|\barchitect\b|\bstructural engineer\b)/.test(lower)) {
    return 'Design';
  }

  if (/(\bepa\b|\bcouncil\b|\bcc inspection\b|\beg inspection\b|\bapproval\b|\bcompliance\b|\bconsent\b|\bbuilding consent\b|\bresource consent\b)/.test(lower)) {
    return 'Consent';
  }

  if (/(\bhandover\b|\bfinal handover\b|\bproject closeout\b|\bclose out\b|\bcompletion certificate\b)/.test(lower)) {
    return 'Handover';
  }

  if (/(\bdemolition\b|\bexcavation\b|\bfoundation\b|\bslab\b|\bdrain\b|\bplumbing\b|\bframe\b|\btruss\b|\broof\b|\bcladding\b|\bgib\b|\bpaint\b|\btile\b|\bkitchen\b|\bflooring\b|\belectrical\b|\binterior\b|\bclean\b|\bwrap\b|\bwindow\b|\bbrick\b|\bplaster\b|\bretaining wall\b|\bjoist\b|\bsteel\b|\bdeck\b|\bdriveway\b|\blandscaping\b|\bfixing\b|\blinings\b|\bfitout\b|\bfit-out\b|\bfit out\b|\bcabinet\b|\bbench\b|\bwardrobe\b|\bsplash back\b|\bsplashback\b|\bbathware\b|\bappliance\b|\bvanity\b|\bmirror\b|\bdoor handle\b|\bstopper\b|\bpaper holder\b|\bskirting\b|\bboxing\b|\bceiling\b|\bstair\b|\bsoffit\b|\bexterior\b|\bexterior door\b|\bgarage door\b|\bdown pipe\b|\bdownpipe\b|\bscaffold\b|\bhirb\b|\binsulation\b|\bglass\b|\block up\b|\blockup\b|\bwaterproof\b|\bback fill\b|\bsite cut\b|\bsilt fence\b|\bsurveyor set out\b|\bfooting\b|\bunder slab\b|\bunder-slab\b|\baquacon\b|\bblock wall\b|\bsite prep\b|\bbase\b|\bground floor\b|\bgf wall\b|\bcivil drain\b|\bdrain work\b|\bservice\b|\btidy up\b|\bsite excavate\b|\btank\b|\bpublic drain\b|\bwall frame\b|\bfloor joist\b|\bsteel frame\b|\bsteel beam\b|\bupper floor\b|\blower truss\b|\bframe inspections\b|\bupper truss\b|\bpreline\b|\bpostline\b|\bgib plaster\b|\bgib fix\b|\binternal linings\b|\bplumber\b|\bfitoff\b|\belec fit\b|\belec final\b|\bplum fit\b|\bplum final\b|\bpower on\b|\bfloor noise\b|\btimber floor\b|\bfloor carpet\b|\bfinal bed\b|\bfinal 2\b|\blouver\b|\belec\b)/.test(lower)) {
    return 'Construction';
  }

  if (/(\bfinal inspection\b|\bowner inspection\b|\bwarranty\b|\bphoto\b|\bccc\b|\bdefect\b|\bmaintenance period\b|\bpractical completion\b|\binsurance change\b|\bcode compliance\b)/.test(lower)) {
    return 'Handover';
  }

  return null;
}

/**
 * Resolve the main stage for a schedule item.
 * Manual / renovation tasks without an explicit stage are Unassigned — not Planning.
 */
export function getMainStage(item: ScheduleItemStageInput): string {
  if (item.main_stage && CANONICAL_STAGE_NAMES.has(item.main_stage)) {
    return item.main_stage;
  }

  if (item.phase && CANONICAL_STAGE_NAMES.has(item.phase)) {
    return item.phase;
  }

  if (item.source_template_item_id) {
    const inferred = inferStageFromTitle(item.title);
    if (inferred) return inferred;
  }

  return UNASSIGNED_STAGE;
}

export function isCanonicalStage(stageName: string): boolean {
  return CANONICAL_STAGE_NAMES.has(stageName);
}

/** Renovation and Other projects use manual schedules (no development template setup). */
export function isManualScheduleProjectType(projectType?: string | null): boolean {
  return projectType === 'Renovation' || projectType === 'Other';
}

/** Default main stage for newly created manual schedule items in renovation-style projects. */
export function defaultMainStageForManualScheduleItem(projectType?: string | null): string | null {
  return isManualScheduleProjectType(projectType) ? 'Construction' : null;
}
